import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Filter, Search, ArrowUpRight, Files, TrendingUp, Award, Clock } from "lucide-react";

const STATUS_LABELS = {
  new: "New", reviewing: "Reviewing", more_info_required: "More info",
  suitable: "Suitable", not_suitable: "Not suitable",
  proposal_in_progress: "Proposal WIP", proposal_sent: "Proposal sent",
  won: "Won", lost: "Lost", on_hold: "On hold",
};

const STATUS_COLORS = {
  new: "bg-blue-50 text-blue-700",
  reviewing: "bg-amber-50 text-amber-700",
  more_info_required: "bg-orange-50 text-orange-700",
  suitable: "bg-emerald-50 text-emerald-700",
  not_suitable: "bg-neutral-100 text-neutral-600",
  proposal_in_progress: "bg-violet-50 text-violet-700",
  proposal_sent: "bg-indigo-50 text-indigo-700",
  won: "bg-bright/15 text-forest",
  lost: "bg-red-50 text-red-700",
  on_hold: "bg-neutral-100 text-neutral-600",
};

const FIT_COLORS = {
  High: "bg-emerald-100 text-emerald-800",
  Medium: "bg-amber-100 text-amber-800",
  Low: "bg-neutral-200 text-neutral-700",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [fit, setFit] = useState("all");
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/api/admin/stats"),
        api.get("/api/admin/applications", { params: { status, fit, q: q || undefined, limit: 200 } }),
      ]);
      setStats(s.data);
      setApps(a.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, fit]);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-2">Operations</p>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-forest">Applications overview</h1>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-kpis">
        <Kpi label="Total" value={stats?.total ?? "—"} icon={Files} />
        <Kpi label="New" value={stats?.new ?? "—"} icon={Clock} tone="blue" />
        <Kpi label="High-fit" value={stats?.high_fit ?? "—"} icon={TrendingUp} tone="green" />
        <Kpi label="Won" value={stats?.won ?? "—"} icon={Award} tone="bright" />
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white border border-black/5 p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input
            placeholder="Search business, contact, suburb or reference..."
            className="pl-9"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="admin-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Filter size={15} className="text-neutral-400" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-filter-status" className="h-10 rounded-md border border-input px-3 text-[13.5px] bg-white text-forest">
            <option value="all">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={fit} onChange={(e) => setFit(e.target.value)} data-testid="admin-filter-fit" className="h-10 rounded-md border border-input px-3 text-[13.5px] bg-white text-forest">
            <option value="all">All fits</option>
            <option value="High">High fit</option>
            <option value="Medium">Medium fit</option>
            <option value="Low">Low fit</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-black/5 overflow-hidden" data-testid="admin-applications-table">
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px] min-w-[900px]">
            <thead className="bg-mist text-forest/70 text-[12px] uppercase tracking-wider">
              <tr>
                <Th>#</Th><Th>Business</Th><Th>Contact</Th><Th>Industry</Th>
                <Th>Site</Th><Th>Bill / mo</Th><Th>Annual kWh</Th>
                <Th>Fit</Th><Th>Status</Th><Th>Submitted</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={11} className="p-8 text-center text-neutral-500">Loading…</td></tr>
              )}
              {!loading && apps.length === 0 && (
                <tr><td colSpan={11} className="p-10 text-center text-neutral-500" data-testid="admin-empty">No applications match your filters yet.</td></tr>
              )}
              {apps.map((a) => (
                <tr key={a.id} className="border-t border-black/5 hover:bg-mist/60" data-testid={`app-row-${a.application_number}`}>
                  <Td mono>{a.application_number}</Td>
                  <Td>
                    <div className="font-semibold text-forest">{a.data.legal_business_name || "—"}</div>
                    <div className="text-[11.5px] text-neutral-500">{a.data.trading_name || ""}</div>
                  </Td>
                  <Td>
                    <div>{a.data.first_name} {a.data.last_name}</div>
                    <div className="text-[11.5px] text-neutral-500">{a.data.email}</div>
                  </Td>
                  <Td className="capitalize">{(a.data.industry || "").replace(/_/g, " ") || "—"}</Td>
                  <Td>{a.data.site_suburb || "—"} {a.data.site_state ? `· ${a.data.site_state}` : ""}</Td>
                  <Td>{a.data.average_monthly_bill || "—"}</Td>
                  <Td>{a.data.annual_kwh || "—"}</Td>
                  <Td>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${FIT_COLORS[a.lead_fit] || FIT_COLORS.Low}`}>
                      {a.lead_fit} · {a.lead_score}
                    </span>
                  </Td>
                  <Td>
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_COLORS[a.status] || "bg-mist text-forest"}`}>
                      {STATUS_LABELS[a.status] || a.status}
                    </span>
                  </Td>
                  <Td className="text-neutral-500 text-[12px]">{(a.submitted_at || "").slice(0, 10)}</Td>
                  <Td>
                    <Link to={`/admin/applications/${a.id}`} data-testid={`open-${a.application_number}`} className="text-forest font-semibold inline-flex items-center gap-1">
                      Open <ArrowUpRight size={14}/>
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tone = "default" }) {
  const toneMap = {
    default: "bg-white",
    blue: "bg-blue-50",
    green: "bg-emerald-50",
    bright: "bg-bright/10",
  };
  return (
    <div className={`rounded-2xl border border-black/5 p-5 ${toneMap[tone]}`}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] tracking-widest uppercase text-neutral-500 font-semibold">{label}</span>
        <Icon size={16} className="text-forest" />
      </div>
      <p className="mt-3 font-heading text-3xl font-semibold text-forest">{value}</p>
    </div>
  );
}
function Th({ children }) { return <th className="text-left px-4 py-3 font-semibold">{children}</th>; }
function Td({ children, mono, className = "" }) {
  return <td className={`px-4 py-3 align-top ${mono ? "font-mono text-[12px] text-neutral-500" : ""} ${className}`}>{children}</td>;
}
